import React, { useState, useEffect } from 'react';
import { XCircle } from 'lucide-react';

const ReferenceSelect = ({ isOpen, onClose, onSave, views, initialReference, projectId }) => {
  const [step, setStep] = useState(1);
  const [selectedView, setSelectedView] = useState(null);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [processedViews, setProcessedViews] = useState([]);
  
  // Process the views to include version index similar to BuildFlow.js
  useEffect(() => {
    if (views) {
      const processed = views.map((view) => {
        const validVersions = (view.versions || []).filter(
          (ver) => !ver.revisionStatus || ver.revisionStatus === "complete"
        );
        
        if (validVersions.length === 0) {
          return null;
        }
        
        // Sort versions by time_created ascending
        validVersions.sort((a, b) => {
          const timeA = new Date(a.time_created || new Date());
          const timeB = new Date(b.time_created || new Date());
          return timeA - timeB; // oldest to newest
        });
        
        // Assign versionIndex (1-based), oldest is version 1, newest is version n
        validVersions.forEach((ver, idx) => {
          ver.versionIndex = idx + 1;
        });

        const newestVersion = validVersions[validVersions.length - 1];

        return {
          ...view,
          versions: validVersions,
          selectedVersionId: newestVersion?.versionId || ""
        };
      }).filter(Boolean);

      setProcessedViews(processed);
    }
  }, [views]);

  // Set initial values if editing an existing reference
  useEffect(() => {
    if (initialReference) {
      setStep(1); // Start at step 1 even when editing
      setSelectedView(initialReference.viewId);
      setSelectedVersionId(initialReference.versionId);
      setExplanation(initialReference.explanation || '');
    }
  }, [initialReference]);

  const handleViewSelect = (e, viewId) => {
    // Prevent form submission when clicking the button
    e.preventDefault();
    
    const view = processedViews.find(v => v._id === viewId);
    setSelectedView(view);
    
    // Default to the selected version in the view (should be the newest)
    setSelectedVersionId(view.selectedVersionId);
  };

  const handleVersionChange = (viewId, versionId) => {
    // If changing version, clear explanation since it might not apply to the new version
    if (selectedVersionId !== versionId && initialReference?.versionId !== versionId) {
      setExplanation('');
    }
    
    setSelectedVersionId(versionId);
    
    // Update the selectedVersionId in the processedViews state
    setProcessedViews(prev =>
      prev.map(v => {
        if (v._id === viewId) {
          return {
            ...v,
            selectedVersionId: versionId
          };
        }
        return v;
      })
    );
  };

  const handleNext = (e) => {
    // Prevent form submission when clicking the button
    e.preventDefault();
    
    if (step === 1 && selectedView) {
      setStep(2);
    }
  };

  const handleBack = (e) => {
    // Prevent form submission when clicking the button
    e.preventDefault();
    
    if (step === 2) {
      setStep(1);
    }
  };

  const handleSave = (e) => {
    // Prevent form submission when clicking the button
    e.preventDefault();
    
    if (!selectedView || !selectedVersionId || !explanation.trim()) {
      return;
    }

    const selectedViewObj = processedViews.find(v => v._id === selectedView._id);
    const selectedVersion = selectedViewObj.versions.find(v => v.versionId === selectedVersionId);
    
    onSave({
      viewId: selectedView._id,
      viewTitle: selectedView.title,
      versionId: selectedVersionId,
      versionIndex: selectedVersion.versionIndex,
      explanation: explanation.trim()
    });
  };

  // Don't render if not open
  if (!isOpen) return null;

  // Find the selected version object for display
  let selectedVersionObj = null;
  if (selectedView && selectedVersionId) {
    const view = processedViews.find(v => v._id === selectedView._id);
    if (view) {
      selectedVersionObj = view.versions.find(v => v.versionId === selectedVersionId);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Navbar */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold">Provide a Reference</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-600 hover:text-black transition-colors duration-200"
          >
            <XCircle size={24} />
          </button>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center p-4 border-b">
          <div className="flex items-center w-full max-w-md">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 flex items-center justify-center rounded-full ${
                step === 1 ? 'bg-black text-white' : 'bg-gray-300 text-white'
              }`}>
                1
              </div>
              <span className={`text-sm mt-1 ${step === 1 ? 'text-black' : 'text-gray-500'}`}>
                Choose a view
              </span>
            </div>
            
            <div className="flex-1 h-0.5 mx-2 bg-gray-300"></div>
            
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 flex items-center justify-center rounded-full ${
                step === 2 ? 'bg-black text-white' : 'bg-gray-300 text-white'
              }`}>
                2
              </div>
              <span className={`text-sm mt-1 ${step === 2 ? 'text-black' : 'text-gray-500'}`}>
                Explain how to use
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Select a view to use as reference</h3>
              
              <div className="space-y-4 max-h-[50vh] overflow-y-auto">
                {processedViews.length === 0 ? (
                  <p className="text-gray-600">No valid views found.</p>
                ) : (
                  processedViews.map((view) => (
                    <div
                      key={view._id}
                      className={`p-4 border rounded flex justify-between items-center ${
                        selectedView?._id === view._id ? 'border-black bg-gray-50' : 'bg-white'
                      }`}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{view.title}</span>
                        {/* Version dropdown */}
                        <div className="inline-block">
                          <select
                            className="version-select mt-2 text-sm text-slate-500"
                            value={view.selectedVersionId}
                            onChange={(e) => handleVersionChange(view._id, e.target.value)}
                          >
                            {view.versions
                              .slice()
                              .sort((a, b) => b.versionIndex - a.versionIndex) // sort descending: most recent first
                              .map((ver) => (
                                <option key={ver.versionId} value={ver.versionId}>
                                  Version {ver.versionIndex}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>

                      <button
                        type="button" // Add type="button" to prevent form submission
                        onClick={(e) => handleViewSelect(e, view._id)}
                        className={`px-3 py-1 rounded ${
                          selectedView?._id === view._id
                            ? 'bg-black text-white'
                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        }`}
                      >
                        {selectedView?._id === view._id ? 'Selected' : 'Select'}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Explain how to use this reference</h3>
              
              {selectedView && selectedVersionObj && (
                <div className="p-4 border rounded bg-gray-50">
                  <p className="font-medium">
                    {selectedView.title} (Version {selectedVersionObj.versionIndex})
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <label className="block text-gray-700">
                  How should we use this reference?
                </label>
                <textarea
                  value={explanation}
                  onChange={(e) => setExplanation(e.target.value)}
                  placeholder="Examples: 'Use the exact same navigation bar style.' 'Replicate the grid design.' 'Copy the footer but change the links.'"
                  className="w-full p-3 border rounded-md"
                  rows={5}
                  required
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t">
          {step === 1 ? (
            <button
              type="button" // Add type="button" to prevent form submission
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
            >
              Cancel
            </button>
          ) : (
            <button
              type="button" // Add type="button" to prevent form submission
              onClick={handleBack}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
            >
              Back
            </button>
          )}
          
          {step === 1 ? (
            <button
              type="button" // Add type="button" to prevent form submission
              onClick={handleNext}
              disabled={!selectedView}
              className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          ) : (
            <button
              type="button" // Add type="button" to prevent form submission
              onClick={handleSave}
              disabled={!explanation.trim()}
              className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferenceSelect;